import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';

const pulseKeyframes = keyframes`
  0%   { transform: scale(1);    box-shadow: 0 0 0 rgba(0,0,0,0); }
  50%  { transform: scale(1.08); box-shadow: 0 0 18px var(--pulse-color); }
  100% { transform: scale(1.05); box-shadow: 0 0 12px var(--pulse-color); }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.size}, 1fr);
  gap: 5px;
  max-width: 560px;
  margin: 0 auto;
  aspect-ratio: 1 / 1;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    max-width: 100%;
    gap: 4px;
  }

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    gap: 3px;
  }
`;

const Cell = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  padding: 0;
  cursor: ${p => p.disabled ? 'default' : 'pointer'};
  border-radius: 6px;
  transition: all 0.25s ease;
  font-family: 'Noto Sans Devanagari', serif;
  min-height: 44px;
  background: ${p => p.palette.cellBg};
  border: 1px solid ${p => p.palette.cellBorder};

  &:hover:not(:disabled) {
    border-color: ${p => p.palette.tappedBorder};
    transform: scale(1.04);
  }

  &:focus-visible {
    outline: 2px solid ${p => p.palette.tappedBorder};
    outline-offset: 2px;
  }

  ${p => p.selected && !p.reducedMotion && css`
    background: ${p.palette.tappedBg};
    border: 2px solid ${p.palette.tappedBorder};
    animation: ${pulseKeyframes} ${p.pulseDurationMs}ms ease-out forwards;
  `}

  ${p => p.selected && p.reducedMotion && css`
    background: ${p.palette.tappedBg};
    border: 2px solid ${p.palette.tappedBorder};
    transform: scale(1.05);
  `}
`;

const CellNumber = styled.span`
  font-size: 9px;
  color: ${p => p.palette.secondaryColor};
  font-family: ${p => p.theme.typography.monoFamily};
  margin-bottom: 2px;
`;

const Primary = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${p => p.palette.primaryColor};
  line-height: 1;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    font-size: 15px;
  }
`;

const Secondary = styled.span`
  font-size: 10px;
  color: ${p => p.palette.secondaryColor};
  font-family: 'Noto Sans Tamil', sans-serif;
  margin-top: 2px;
  line-height: 1;
`;

function prefersReducedMotion() {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

const SingleTapGrid = ({
  size,
  cells,
  palette,
  pulseDurationMs = 700,
  onCellSelect,
  ariaLabel,
}) => {
  const [selectedCellNumber, setSelectedCellNumber] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timeoutRef = useRef(null);

  const handleCellTap = useCallback((cellNumber) => {
    if (isAnimating) return;
    setSelectedCellNumber(cellNumber);
    setIsAnimating(true);
    const rm = prefersReducedMotion();
    setReducedMotion(rm);

    const delay = rm ? 0 : pulseDurationMs;
    timeoutRef.current = setTimeout(() => {
      onCellSelect(cellNumber);
    }, delay);
  }, [isAnimating, onCellSelect, pulseDurationMs]);

  useEffect(() => {
    const t = timeoutRef.current;
    return () => { if (t) clearTimeout(t); };
  }, []);

  const cssVars = {
    '--pulse-color': palette.pulseColor || palette.pathGlow || 'rgba(0,0,0,0.5)',
  };

  return (
    <GridContainer
      role="grid"
      aria-label={ariaLabel}
      size={size}
      style={cssVars}
    >
      {cells.map(c => {
        const isSelected = selectedCellNumber === c.cellNumber;
        return (
          <Cell
            key={c.cellNumber}
            role="gridcell"
            aria-label={`Cell ${c.cellNumber}, ${c.primary}`}
            palette={palette}
            pulseDurationMs={pulseDurationMs}
            selected={isSelected}
            reducedMotion={reducedMotion}
            onClick={() => handleCellTap(c.cellNumber)}
            disabled={isAnimating}
            tabIndex={isAnimating ? -1 : 0}
          >
            <CellNumber palette={palette}>{c.cellNumber}</CellNumber>
            <Primary palette={palette}>{c.primary}</Primary>
            {c.secondary && <Secondary palette={palette}>{c.secondary}</Secondary>}
          </Cell>
        );
      })}
    </GridContainer>
  );
};

export default SingleTapGrid;
